import {createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../use-is-mounted";
import Api from "../../../core/services/api/api";
import axios from "axios";
import {createUUId} from "../../../core/services/utils/utils";

const CachedCountryProvinceControllerContext = createContext({
    getCountries: async () => void 0,
    getProvincesForCountry: async () => void 0,
});

export const CachedCountryProvinceProvider = ({children}) => {
    const countriesCache = useRef();
    const provincesCache = useRef({});
    const [fetchCountriesQueue, setFetchCountriesQueue] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const loadingCountriesRef = useRef(false);
    const [fetchProvincesQueue, setFetchProvincesQueue] = useState([]);
    const [loadingProvincesForCountries, setLoadingProvincesForCountries] = useState([]);
    const loadingProvincesForCountriesRef = useRef([]);
    const processedIds = useRef([]);
    const isMounted = useIsMounted();

    /**
     * Fetches the list of countries from the server.
     */
    const getCountries = useCallback(async () => {
        if (countriesCache.current) {
            return countriesCache.current;
        }

        loadingCountriesRef.current = true;
        setLoadingCountries(true);
        const response = await Api.getAllCountries();
        if (!isMounted())
            return;
        loadingCountriesRef.current = false;
        setLoadingCountries(false);

        if (response?.resultFlag) {
            countriesCache.current = response.data?.map(e => ({
                ...e,
                provinces: [],
            })) ?? [];
        } else {
            countriesCache.current = [];
        }
        return countriesCache.current;
    }, [isMounted])

    /**
     * Fetches the provinces of the provided country id
     */
    const getProvincesForCountry = useCallback(async (countryId, cancelToken) => {
        setLoadingProvincesForCountries(prevState => [...prevState, countryId]);
        loadingProvincesForCountriesRef.current.push(countryId);
        if (provincesCache.current[countryId]) {
            setLoadingProvincesForCountries(prevState => prevState.filter(e => e !== countryId));
            loadingProvincesForCountriesRef.current = loadingProvincesForCountriesRef.current.filter(e => e !== countryId);
            return provincesCache.current[countryId];
        }

        const response = await Api.getAllProvincesByCountryId(countryId, cancelToken);
        if (!isMounted())
            return;

        if (response?.resultFlag) {
            provincesCache.current[countryId] = response.data ?? [];
        }
        setLoadingProvincesForCountries(prevState => prevState.filter(e => e !== countryId));
        loadingProvincesForCountriesRef.current = loadingProvincesForCountriesRef.current.filter(e => e !== countryId);

        return provincesCache.current[countryId] ?? [];
    }, [isMounted])

    /**
     * With each change in the fetchCountriesQueue:
     * - Fetches the countries from the server for each item in the queue.
     */
    useEffect(() => {
        if (loadingCountriesRef.current)
            return;
        const first = fetchCountriesQueue.find(e => !processedIds.current.includes(e.id));
        if (!first)
            return;
        processedIds.current.push(first.id);
        getCountries().then((val) => {
            setFetchCountriesQueue(prevState => prevState.filter(e => e.id !== first.id))
            processedIds.current = processedIds.current.filter(e => e !== first.id)
            first.resolve(val);
        });
    }, [fetchCountriesQueue, loadingCountries, getCountries])

    /**
     * With each change in the fetchProvincesQueue:
     * - Fetches the provinces from the server for each item in the queue.
     */
    useEffect(() => {
        const first = fetchProvincesQueue.find(e => !processedIds.current.includes(e.id));
        if (!first)
            return;
        if (loadingProvincesForCountriesRef.current.includes(first.countryId))
            return;
        processedIds.current.push(first.id);
        getProvincesForCountry(first.countryId).then((val) => {
            if (isMounted()) {
                setFetchProvincesQueue(prevState => prevState.filter(e => e.id !== first.id))
            }
            processedIds.current = processedIds.current.filter(e => e !== first.id)
            first.resolve(val);
        });
    }, [isMounted, fetchProvincesQueue, loadingProvincesForCountries, getProvincesForCountry])

    /**
     * Fetches the countries using a queue to prevent multiple calls.
     * @type {function(): Promise<unknown>}
     */
    const getCountriesQueue = useCallback(async () => {
        return await new Promise(r => {
            setFetchCountriesQueue(prevState => [...prevState, {resolve: r, id: createUUId()}])
        })
    }, [])

    /**
     * Fetches the provinces for the given country id using a queue to prevent multiple calls for the same country.
     * @type {function(*): Promise<unknown>}
     */
    const getProvincesForCountryQueue = useCallback(async (countryId) => {
        return await new Promise(r => {
            setFetchProvincesQueue(prevState => [...prevState, {
                resolve: r,
                id: createUUId(),
                countryId: countryId,
            }])
        })
    }, [])

    const controller = useMemo(() => ({
        getCountries: getCountriesQueue,
        getProvincesForCountry: getProvincesForCountryQueue,
    }), [getCountriesQueue, getProvincesForCountryQueue])

    return (
        <CachedCountryProvinceControllerContext.Provider value={controller}>
            {children}
        </CachedCountryProvinceControllerContext.Provider>
    );
}

/**
 * This hook caches the countries and provinces for each country to prevent later api calls to fetch the
 * provinces.
 *
 * @param {number | undefined} initialCountryId
 * @param {boolean} loadAllProvincesAtOnce
 */
const useCountryProvinceCache = (initialCountryId = undefined, loadAllProvincesAtOnce = false) => {
    const {
        getCountries: getAllCountries,
        getProvincesForCountry: getProvinces
    } = useContext(CachedCountryProvinceControllerContext);

    const [countries, setCountries] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(true);
    const [loadedProvincesForCountries, setLoadedProvincesForCountries] = useState([]);
    const [loadingProvincesForCountry, setLoadingProvincesForCountry] = useState([]);
    const isMounted = useIsMounted();
    const fetchedAllProvincesRef = useRef(false);

    /**
     * Memoizes the list of country IDs.
     * Since the countries state changes whenever a province is loaded, we need a stable list of IDs to use in the dependencies.
     */
    const countryIdsStr = useMemo(() => countries.map(c => c.id).join(','), [countries]);
    const countryIds = useMemo(() => {
        if (!countryIdsStr) return [];
        return countryIdsStr.split(',').map(Number);
    }, [countryIdsStr]);

    /**
     * Loads the list of provinces for the given countryId and then saves it in the state for future references.
     * @param {number} countryId
     */
    const getProvincesForCountry = useCallback(async (countryId) => {
        setLoadingProvincesForCountry(prevState => [...prevState, countryId]);
        const provinces = await getProvinces(countryId);

        if (!isMounted())
            return;

        if (provinces) {
            setCountries(prevState => prevState?.map(e => e.id === countryId
                ? {...e, provinces: provinces ?? []}
                : e
            ))
            setLoadedProvincesForCountries(prevState => [...prevState, countryId])
        }
        setLoadingProvincesForCountry(prevState => prevState.filter(e => e !== countryId));
    }, [isMounted, getProvinces]);


    /**
     * Determines whether we need to fetch the provinces of the selected country from the server.
     * @param {number} countryId
     */
    const onCountrySelected = useCallback((countryId) => {
        const parsedCountryId = Number(countryId);
        if (Number.isNaN(parsedCountryId))
            return;
        const country = countries?.find(e => e.id === parsedCountryId);
        if (!country)
            return;
        if (!loadedProvincesForCountries.find(e => parsedCountryId === e)) {
            getProvincesForCountry(country?.id).then();
        }
    }, [countries, getProvincesForCountry, loadedProvincesForCountries])

    /**
     * Fetches the list of countries from the server and if the result of the api is successful, sets
     * the list.
     */
    const getCountries = useCallback(async () => {
        setLoadingCountries(true);
        const countries = await getAllCountries();

        if (!isMounted())
            return;

        setLoadingCountries(false);
        if (countries) {
            setCountries(countries);
        }
    }, [isMounted, getAllCountries]);

    /**
     * As soon as the component mounts:
     * - fetches all the countries of the system from the server.
     */
    useLayoutEffect(() => {
        getCountries().then();
    }, [getCountries])

    /**
     * With each change in the initialCountryId:
     * - fetches the provinces for the provided countryId if the provinces are not yet loaded for that country
     */
    useLayoutEffect(() => {
        if (!initialCountryId || !countries.length)
            return;
        onCountrySelected(initialCountryId);
    }, [initialCountryId, onCountrySelected, countries])


    /**
     * Fetches all the provinces for all countries at once if the loadAllProvincesAtOnce is true.
     * @type {(function(*): Promise<void>)}
     */
    const fetchAllProvinces = useCallback(async (ids, cancelToken) => {
        if (!ids?.length) return;

        if (!isMounted())
            return;
        const promises = ids.map(id => getProvincesForCountry(id, cancelToken));
        await Promise.all(promises);
    }, [getProvincesForCountry, isMounted])

    /**
     * If the loadAllProvincesAtOnce is true, fetches all the provinces for all countries at once.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        if (loadAllProvincesAtOnce && countryIds.length && !fetchedAllProvincesRef.current) {
            fetchedAllProvincesRef.current = true;
            fetchAllProvinces(countryIds, cancelTokenSource.token).then();
        }
        return () => {
            cancelTokenSource.cancel();
        }
    }, [loadAllProvincesAtOnce, fetchAllProvinces, countryIds])

    return useMemo(() => ({
        loadingCountries,
        loadingProvincesForCountry,
        countries,
        onCountrySelected,
    }), [loadingCountries, loadingProvincesForCountry, countries, onCountrySelected])
}

export default useCountryProvinceCache;
